import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { cn } from '../../utils/cn';
interface LegalDoc {
  slug: string;
  title: string;
  updated: string;
  intro: string;
  sections: {
    heading: string;
    body: string[];
  }[];
}
const docs: LegalDoc[] = [{
  slug: 'terms',
  title: 'Terms of service',
  updated: '12 August 2026',
  intro: 'Pan & Apron is a marketplace. We introduce guests to independent private chefs, host the conversation, and handle the money. We do not employ chefs and we do not cook.',
  sections: [{
    heading: '1. What Pan & Apron is',
    body: ['Pan & Apron operates a platform where independent chefs publish menus and guests request bookings. The contract for the cooking service is between the guest and the chef. Pan & Apron is not a party to it, except as payment agent.', 'Chefs set their own menus, prices, minimum head counts, service areas, and cancellation tiers. Pan & Apron does not set or guarantee prices.']
  }, {
    heading: '2. Bookings and quotes',
    body: ['A request is not a booking. A booking exists only once a guest accepts a quote and the deposit is authorized.', 'Quotes expire 48 hours after they are sent. A revised quote supersedes the previous version, and both remain visible in the booking history.', 'Where groceries are billed at cost, the chef must attach receipts before the balance is charged.']
  }, {
    heading: '3. Fees',
    body: ['Guests pay a 10% service fee shown on the quote. Chefs receive the line total less that fee. Taxes shown are estimates based on the service address and are finalized on the invoice.']
  }, {
    heading: '4. Conduct',
    body: ['Guests provide a safe, legal, and accessible kitchen as described in their kitchen profile. Chefs arrive with the equipment they have said they will bring and hold the certifications shown on their profile.', 'Accounts that misrepresent allergies, credentials, or kitchen facilities may be removed.']
  }, {
    heading: '5. Prototype notice',
    body: ['This is a design prototype. No payments are processed and no contracts are formed.']
  }]
}, {
  slug: 'privacy',
  title: 'Privacy policy',
  updated: '12 August 2026',
  intro: 'What is public, what is private, and what the chef sees only after you book.',
  sections: [{
    heading: 'Public on your profile',
    body: ['Your first name, your photo, your city, and the reviews you write. Nothing else is public.']
  }, {
    heading: 'Shared with a chef when you request',
    body: ['Your dietary profile (allergens and severities, intolerances, diets, dislikes, spice tolerance), your kitchen profile and its photos, your neighbourhood, and the occasion details.', 'Your exact address and phone number are released only when a booking is confirmed, and only to that chef.']
  }, {
    heading: 'Kept private always',
    body: ['Payment details, full date of birth, government ID, and your message history with support.']
  }, {
    heading: 'Chat translation',
    body: ['When auto-translation is on, message text is processed for translation. The original text is always retained and shown on request.']
  }, {
    heading: 'Retention',
    body: ['Booking records are kept for seven years for tax purposes. Chats are kept for two years.']
  }]
}, {
  slug: 'cookies',
  title: 'Cookie notice',
  updated: '4 June 2026',
  intro: 'We use the smallest set of cookies that keeps a booking working.',
  sections: [{
    heading: 'Essential',
    body: ['Session, language, currency, and theme. These cannot be turned off without breaking the site.']
  }, {
    heading: 'Analytics',
    body: ['Aggregated page and funnel measurement. Off by default in the EU and UK until you opt in.']
  }, {
    heading: 'No advertising cookies',
    body: ['We do not run ad retargeting and we do not sell browsing data.']
  }]
}, {
  slug: 'allergens',
  title: 'Allergen & food safety notice',
  updated: '30 July 2026',
  intro: 'Read this one properly. Allergy management on Pan & Apron is structured and enforced, but it is not a medical guarantee.',
  sections: [{
    heading: 'What we require of chefs',
    body: ['Every chef must hold a current food hygiene certificate to publish a listing.', 'A chef cannot send a quote until they have acknowledged the guest’s allergy record for that booking.', 'Chefs must declare whether they can offer allergen-separated prep. Where they cannot, the listing says so.']
  }, {
    heading: 'What we require of guests',
    body: ['Record every allergen with an accurate severity — avoid, strict, or anaphylaxis risk — for each person eating.', 'Tell the chef in chat if anything changes, including additional guests.']
  }, {
    heading: 'The limits',
    body: ['Cooking happens in home kitchens. Cross-contact cannot be eliminated in a domestic environment, and ingredient labelling is outside our control.', 'If a reaction is life-threatening, call emergency services first. Anyone with anaphylaxis risk should carry their own adrenaline auto-injector.']
  }]
}, {
  slug: 'waiver',
  title: 'Liability waiver',
  updated: '30 July 2026',
  intro: 'What you agree to when a stranger cooks with fire in your home.',
  sections: [{
    heading: 'Cooking in your home',
    body: ['You confirm your kitchen is safe to use, that appliances are in working order, and that smoke detection is functional.', 'Open-flame and live-fire menus require outdoor space, landlord permission where applicable, and a working extinguisher. The chef may refuse to cook if these are missing on arrival.']
  }, {
    heading: 'Property and injury',
    body: ['Chefs carry their own liability insurance where their profile shows an insurance badge. Claims for damage or injury are made against the chef’s policy; Pan & Apron assists but is not the insurer.']
  }, {
    heading: 'Alcohol',
    body: ['Where a chef provides a pairing, they do not hold a liquor licence. Service is to adults in a private residence only.']
  }]
}, {
  slug: 'cancellations',
  title: 'Cancellation policy',
  updated: '12 August 2026',
  intro: 'Each chef picks one of three tiers. The tier is shown on the profile and printed on the quote.',
  sections: [{
    heading: 'Flexible',
    body: ['Full deposit refund up to 48 hours before service. Inside 48 hours, half the deposit is retained.']
  }, {
    heading: 'Moderate',
    body: ['Full deposit refund up to 7 days before. Inside 7 days, the deposit is retained. Inside 24 hours, 50% of the total is due.']
  }, {
    heading: 'Strict',
    body: ['Deposit is non-refundable once accepted, because sourcing starts early. Inside 72 hours, 75% of the total is due.']
  }, {
    heading: 'If the chef cancels',
    body: ['You are refunded in full, we help you re-book, and the chef’s acceptance rate reflects it.']
  }, {
    heading: 'At-cost groceries',
    body: ['Where a chef has already shopped, documented grocery costs are payable regardless of tier.']
  }]
}, {
  slug: 'disclaimers',
  title: 'Disclaimers',
  updated: '4 June 2026',
  intro: 'Plain statements about what this platform is and is not.',
  sections: [{
    heading: 'Not a restaurant',
    body: ['Chefs cook in private homes or their own certified kitchens. Pan & Apron holds no food licence of its own.']
  }, {
    heading: 'Not nutrition advice',
    body: ['Menu descriptions are written by chefs. Calorie, macro, and nutritional claims are not verified.']
  }, {
    heading: 'Availability',
    body: ['Chef availability, pricing, and service areas change. What you see is the chef’s latest published listing.']
  }, {
    heading: 'Prototype',
    body: ['This build is a design prototype. Payments, payouts, verification, and translation are represented, not live.']
  }]
}, {
  slug: 'chef-terms',
  title: 'Chef terms',
  updated: '12 August 2026',
  intro: 'The commitments that come with a listing.',
  sections: [{
    heading: 'Independence',
    body: ['You are an independent operator. You set your menus, prices, and calendar, and you are responsible for your own taxes.']
  }, {
    heading: 'Credentials',
    body: ['A current food hygiene certificate and verified ID are required to publish. Insurance and background checks are optional badges, and their absence is shown to guests.']
  }, {
    heading: 'Quoting',
    body: ['Quotes must reflect the published menu. Add-ons must be itemized. You must acknowledge the guest’s allergy record before sending.', 'Where you bill groceries at cost, receipts must be uploaded before the balance is charged.']
  }, {
    heading: 'Payouts',
    body: ['Deposits release on confirmation, balances after service, and payouts run on the 1st and 16th. The 10% platform fee is shown on every line.']
  }]
}];
export function Legal() {
  const {
    doc
  } = useParams();
  const active = docs.find(d => d.slug === doc) ?? docs[0];
  return <div className="mx-auto grid max-w-shell gap-10 px-5 py-14 lg:grid-cols-[240px_minmax(0,1fr)] lg:px-8 lg:py-16" data-id="element-2299" data-mp="1xkrko9xzle9t">
      <nav aria-label="Legal documents" className="lg:sticky lg:top-24 lg:self-start" data-id="element-2300" data-mp="1sm6m0eydv2nh">
        <h2 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-ink-3" data-id="element-2301" data-mp="225cojp0ivhs0">Policies</h2>
        <ul className="mt-3 space-y-0.5" data-id="element-2302" data-mp="1x7upqrg7yixq">
          {docs.map(d => <li key={d.slug} data-id="element-2303" data-mp="2ay7z8y8re5p6">
              <Link to={`/legal/${d.slug}`} className={cn('block rounded-lg px-3 py-2 text-sm transition-colors duration-150 ease-out', d.slug === active.slug ? 'bg-primary-soft font-medium text-primary' : 'text-ink-2 hover:bg-oat hover:text-ink')} data-id="element-2304" data-mp="2azj3ez649fgt">
                {d.title}
              </Link>
            </li>)}
        </ul>
      </nav>

      <article className="max-w-[72ch]" data-id="element-2305" data-mp="2pufi9v8fl12v">
        <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-action" data-id="element-2306" data-mp="2pvgw27gu01oa">Legal</p>
        <h1 className="mt-3 font-display text-[clamp(1.9rem,3.2vw,2.8rem)] font-semibold leading-tight tracking-[-0.02em] text-ink" data-id="element-2307" data-mp="zij7zuoh2kik">
          {active.title}
        </h1>
        <p className="mt-2 text-[13px] text-ink-3" data-id="element-2308" data-mp="le0ssysqcm2n">Last updated {active.updated}</p>
        <p className="mt-6 text-lg leading-relaxed text-ink-2" data-id="element-2309" data-mp="pekr45p1hvqd">{active.intro}</p>

        <div className="mt-10 space-y-9" data-id="element-2310" data-mp="bn68w12yhykd">
          {active.sections.map(section => <section key={section.heading} data-id="element-2311" data-mp="glr75uyne866">
              <h2 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink" data-id="element-2312" data-mp="gkpszhmc55k8">{section.heading}</h2>
              <div className="mt-3 space-y-3" data-id="element-2313" data-mp="1nf9bkzjgtdf">
                {section.body.map(paragraph => <p key={paragraph.slice(0, 24)} className="text-[15px] leading-relaxed text-ink-2" data-id="element-2314" data-mp="6p4p1dxphv7u">
                    {paragraph}
                  </p>)}
              </div>
            </section>)}
        </div>

        <p className="mt-12 rounded-xl border border-hairline bg-oat p-5 text-sm text-ink-2" data-id="element-2315" data-mp="1n2cp31vx90z0">
          Questions about any of this go to <Link to="/contact" className="font-medium text-primary underline underline-offset-4" data-id="element-2316" data-mp="1s04sas20o9ii">our contact form</Link>, or
          open a ticket from your account if it concerns a live booking.
        </p>
      </article>
    </div>;
}